/**
 * Componente para exibir relatórios de sessões da empresa
 * 
 * @author Equipe GestaoeCRM
 * @date 02/10/2025
 */

import { useQuery } from '@tanstack/react-query';
import { workSessionReportsApi } from '@/services/workSessionReportsApi';
import { useAuth } from '@/contexts/AuthContext';
import LoadingSpinner from './LoadingSpinner';
import { 
  Activity, 
  CheckCircle, 
  Clock, 
  DollarSign, 
  RefreshCw,
  TrendingUp,
  Users
} from 'lucide-react';
import { useState } from 'react';

interface CompanyStats {
  totalSessions: number;
  activeSessions: number;
  completedSessions: number;
  totalHours: number;
  totalValue: number;
  sessionsByUser: Array<{
    userId: string;
    userName: string;
    sessionCount: number;
    totalHours: number;
    totalValue: number;
    hasHourRate?: boolean;
  }>;
  hourlyRate: number;
  usersWithoutHourRate?: Array<{
    userId: string;
    userName: string;
    sessionCount: number;
    totalHours: number;
  }>;
}

export default function CompanyWorkSessionReports() {
  const { user } = useAuth();
  const [timeRange, setTimeRange] = useState<'today' | 'week' | 'month'>('today');

  // Buscar estatísticas da empresa
  const { data: statsData, isLoading, error, refetch } = useQuery({
    queryKey: ['company-work-session-stats', timeRange],
    queryFn: async () => {
      // console.log('═══════════════════════════════════════════════════');
      // console.log('🔍 [CompanyWorkSessionReports] INICIANDO BUSCA DE ESTATÍSTICAS');
      // console.log('═══════════════════════════════════════════════════');
      // console.log('📋 Parâmetros:', {
      // timeRange,
      // userEmail: user?.email,
      // userRole: user?.role,
      // userId: user?.id,
      // tenantId: user?.tenantId
      // });
      
      try {
        const result = await workSessionReportsApi.getCompanyStats(timeRange);
        // console.log('✅ [CompanyWorkSessionReports] RESPOSTA RECEBIDA:');
        // console.log('📦 Resposta completa:', JSON.stringify(result, null, 2));
        // console.log('📊 Dados extraídos:', result?.data);
        // console.log('═══════════════════════════════════════════════════');
        return result;
      } catch (err) {
        // console.error('❌ [CompanyWorkSessionReports] ERRO NA REQUISIÇÃO:');
        // console.error('Erro completo:', err);
        // console.error('═══════════════════════════════════════════════════');
        throw err;
      }
    },
    enabled: !!user && (user.role === 'ADMIN' || user.role === 'GESTOR'),
    refetchInterval: 30000, // Atualizar a cada 30 segundos
    onError: (err: any) => {
      // console.error('═══════════════════════════════════════════════════');
      // console.error('❌ [CompanyWorkSessionReports] CALLBACK DE ERRO');
      // console.error('Tipo de erro:', err?.constructor?.name);
      // console.error('Mensagem:', err?.message);
      // console.error('Response:', err?.response);
      // console.error('═══════════════════════════════════════════════════');
    },
    onSuccess: (data: any) => {
      // console.log('═══════════════════════════════════════════════════');
      // console.log('✅ [CompanyWorkSessionReports] CALLBACK DE SUCESSO');
      // console.log('Estrutura dos dados:', {
      // hasData: !!data,
      // hasDataProperty: !!data?.data,
      // hasDataData: !!data?.data?.data,
      // dataKeys: data ? Object.keys(data) : [],
      // dataDataKeys: data?.data ? Object.keys(data.data) : []
      // });
      // console.log('Dados finais:', data?.data);
      // console.log('═══════════════════════════════════════════════════');
    }
  });

  const handleRefresh = () => {
    refetch();
  };

  const handleTimeRangeChange = (range: 'today' | 'week' | 'month') => {
    setTimeRange(range);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-500">Erro ao carregar estatísticas da empresa</p>
        <button
          onClick={handleRefresh}
          className="mt-2 px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  // CORREÇÃO: Os dados vêm em statsData.data, não statsData.data.data
  const stats: CompanyStats = statsData?.data || {
    totalSessions: 0,
    activeSessions: 0,
    completedSessions: 0,
    totalHours: 0,
    totalValue: 0,
    sessionsByUser: [],
    hourlyRate: 0,
    usersWithoutHourRate: []
  };

  // console.log('═══════════════════════════════════════════════════');
  // console.log('📈 [CompanyWorkSessionReports] STATS PROCESSADOS:');
  // console.log('═══════════════════════════════════════════════════');
  // console.log('statsData completo:', statsData);
  // console.log('statsData.data:', statsData?.data);
  // console.log('statsData.data.data:', statsData?.data?.data);
  // console.log('Stats finais usados no render:', {
  // totalSessions: stats.totalSessions,
  // activeSessions: stats.activeSessions,
  // completedSessions: stats.completedSessions,
  // totalHours: stats.totalHours,
  // totalValue: stats.totalValue,
  // sessionsByUser: stats.sessionsByUser?.length || 0,
  // hourlyRate: stats.hourlyRate
  // });
  // console.log('═══════════════════════════════════════════════════');

  const formatHours = (hours: number) => {
    const wholeHours = Math.floor(hours);
    const minutes = Math.round((hours - wholeHours) * 60);
    return `${wholeHours}h ${minutes}min`;
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const getTimeRangeText = (range: string) => {
    switch (range) {
      case 'today':
        return 'Hoje';
      case 'week':
        return 'Esta Semana';
      case 'month':
        return 'Este Mês';
      default:
        return 'Hoje';
    }
  };

  return (
    <div className="space-y-6">
      {/* Alerta: Usuários sem valor por hora */}
      {stats.usersWithoutHourRate && stats.usersWithoutHourRate.length > 0 && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-lg">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-yellow-800">
                Atenção: Usuários sem valor por hora definido
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p className="mb-2">Os seguintes usuários possuem sessões de trabalho mas não têm valor por hora cadastrado:</p>
                <ul className="list-disc list-inside space-y-1">
                  {stats.usersWithoutHourRate.map((user) => (
                    <li key={user.userId}>
                      <strong>{user.userName}</strong> - {user.sessionCount} sessão(ões), {formatHours(user.totalHours)}
                    </li>
                  ))}
                </ul>
                <p className="mt-2">
                  Por favor, cadastre os valores por hora na página <a href="/hour-rates" className="font-medium underline hover:text-yellow-900">Valores por Hora</a> para que o cálculo de valores seja preciso.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Header com controles */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Activity className="h-5 w-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900">Relatórios de Sessões</h3>
        </div>
        <div className="flex items-center space-x-2">
          {/* Seletor de período */}
          <div className="flex items-center space-x-1 bg-gray-100 rounded-lg p-1">
            {(['today', 'week', 'month'] as const).map((range) => (
              <button
                key={range}
                onClick={() => handleTimeRangeChange(range)}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                  timeRange === range
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {getTimeRangeText(range)}
              </button>
            ))}
          </div>
          
          {/* Botão de atualizar */}
          <button
            onClick={handleRefresh}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
            title="Atualizar dados"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Cards de estatísticas */}
      <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
        {/* Sessões Ativas */}
        <div className="rounded-lg border bg-green-50 text-green-600 border-green-100 p-4 transition-all duration-200 hover:shadow-md">
          <div className="flex items-center justify-between mb-2">
            <Activity className="h-5 w-5" />
            <TrendingUp className="h-4 w-4" />
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-1">Sessões Ativas</h3>
            <div className="text-2xl font-bold text-gray-900 mb-1">{stats.activeSessions}</div>
            <p className="text-xs text-gray-500">Em andamento</p>
          </div>
        </div>

        {/* Sessões Concluídas */}
        <div className="rounded-lg border bg-blue-50 text-blue-600 border-blue-100 p-4 transition-all duration-200 hover:shadow-md">
          <div className="flex items-center justify-between mb-2">
            <CheckCircle className="h-5 w-5" />
            <TrendingUp className="h-4 w-4" />
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-1">Concluídas</h3>
            <div className="text-2xl font-bold text-gray-900 mb-1">{stats.completedSessions}</div>
            <p className="text-xs text-gray-500">{getTimeRangeText(timeRange)}</p>
          </div>
        </div>

        {/* Horas Trabalhadas */}
        <div className="rounded-lg border bg-purple-50 text-purple-600 border-purple-100 p-4 transition-all duration-200 hover:shadow-md">
          <div className="flex items-center justify-between mb-2">
            <Clock className="h-5 w-5" />
            <TrendingUp className="h-4 w-4" />
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-1">Horas Trabalhadas</h3>
            <div className="text-2xl font-bold text-gray-900 mb-1">{formatHours(stats.totalHours)}</div>
            <p className="text-xs text-gray-500">{stats.totalHours.toFixed(1)}h precisas</p>
          </div>
        </div>

        {/* Valor Gerado */}
        <div className="rounded-lg border bg-yellow-50 text-yellow-600 border-yellow-100 p-4 transition-all duration-200 hover:shadow-md">
          <div className="flex items-center justify-between mb-2">
            <DollarSign className="h-5 w-5" />
            <TrendingUp className="h-4 w-4" />
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-1">Valor Gerado</h3>
            <div className="text-2xl font-bold text-gray-900 mb-1">{formatCurrency(stats.totalValue)}</div>
            <p className="text-xs text-gray-500">{getTimeRangeText(timeRange)}</p>
          </div>
        </div>
      </div>

      {/* Estatísticas por usuário */}
      {stats.sessionsByUser.length > 0 && (
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-4">
            <Users className="h-5 w-5 text-gray-600" />
            <h4 className="text-lg font-medium text-gray-900">Performance por Funcionário</h4>
          </div>
          
          <div className="space-y-3">
            {stats.sessionsByUser.map((userStats) => (
              <div key={userStats.userId} className="bg-white rounded-lg p-4 border border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <h5 className="font-medium text-gray-900">{userStats.userName}</h5>
                  <span className="text-sm text-gray-500">{userStats.sessionCount} sessões</span>
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Horas:</span>
                    <span className="ml-2 font-medium">{formatHours(userStats.totalHours)}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Valor:</span>
                    <span className="ml-2 font-medium">{formatCurrency(userStats.totalValue)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Resumo geral */}
      <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
        <div className="flex items-center space-x-2 mb-2">
          <TrendingUp className="h-5 w-5 text-blue-600" />
          <h4 className="text-lg font-medium text-gray-900">Resumo Geral</h4>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <span className="text-gray-600">Total de Sessões:</span>
            <span className="ml-2 font-medium text-gray-900">{stats.totalSessions}</span>
          </div>
          <div>
            <span className="text-gray-600">Taxa Média por Hora:</span>
            <span className="ml-2 font-medium text-gray-900">{formatCurrency(stats.hourlyRate)}</span>
          </div>
          <div>
            <span className="text-gray-600">Período:</span>
            <span className="ml-2 font-medium text-gray-900">{getTimeRangeText(timeRange)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
